import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { fetchHomeDisplayConfig } from '@/api/member';
import { readMemberSession, refreshCurrentMember, type MemberSession } from '@/utils/memberSession';

interface ShareRewardDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ShareRewardFloat() {
  const { t } = useTranslation('cinema');
  const [open, setOpen] = useState(false);

  return <>
    <button
      type="button"
      onClick={() => setOpen(true)}
      title={t('cinema_shareEarnPoints')}
      aria-label={t('cinema_shareEarnPoints')}
      className="fixed bottom-[calc(4.65rem+env(safe-area-inset-bottom))] right-3 z-40 flex w-[66px] flex-col items-center gap-1 text-accent-400 transition-transform hover:scale-105 md:bottom-6 md:right-6"
    >
      <span className="flex h-14 w-14 items-center justify-center [perspective:160px]">
        <img
          src="/images/reward-coin.webp"
          alt=""
          aria-hidden="true"
          className="animate-coin-spin-y-forward h-14 w-14 object-contain drop-shadow-[0_9px_15px_rgba(0,0,0,0.55)]"
        />
      </span>
      <span className="whitespace-nowrap text-[11px] font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">{t('cinema_shareEarnPoints')}</span>
    </button>
    <ShareRewardDialog open={open} onClose={() => setOpen(false)} />
  </>;
}

export function ShareRewardDialog({ open, onClose }: ShareRewardDialogProps) {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [copied, setCopied] = useState(false);
  const [inviteRewardPoints, setInviteRewardPoints] = useState(20);
  const copiedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    void fetchHomeDisplayConfig()
      .then((config) => {
        const points = Number(config.inviteRewardPoints);
        setInviteRewardPoints(Number.isFinite(points) ? Math.max(0, points) : 20);
      })
      .catch(() => setInviteRewardPoints(20));
  }, []);

  useEffect(() => {
    const syncMember = () => setMember(readMemberSession());
    window.addEventListener('member-session-change', syncMember);
    return () => window.removeEventListener('member-session-change', syncMember);
  }, []);

  useEffect(() => {
    if (!open) return;
    const current = readMemberSession();
    setMember(current);
    setCopied(false);
    if (current.isLoggedIn && !current.shareCode) {
      void refreshCurrentMember().then(setMember).catch(() => undefined);
    }
  }, [open]);

  useEffect(() => () => {
    if (copiedTimerRef.current != null) window.clearTimeout(copiedTimerRef.current);
  }, []);

  const shareLink = useMemo(() => {
    if (!member.shareCode || typeof window === 'undefined') return '';
    return member.shareUrl || `${window.location.origin}/?ref=${encodeURIComponent(member.shareCode)}`;
  }, [member.shareCode, member.shareUrl]);

  const copyShareLink = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      const input = document.createElement('textarea');
      input.value = shareLink;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(true);
    if (copiedTimerRef.current != null) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => setCopied(false), 1800);
  }, [shareLink]);

  const closeAndNavigate = useCallback((path: string) => {
    if (copiedTimerRef.current != null) {
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
    setCopied(false);
    onClose();
    navigate(path);
  }, [navigate, onClose]);

  if (!open) return null;

  return <div className="cinema-modal-safe fixed inset-0 z-[70] flex items-center justify-center px-4 pt-4 sm:p-5">
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
    <div className="relative z-10 w-full max-w-md rounded-2xl border border-background-800 bg-background-900 p-5 shadow-2xl sm:p-6">
      <button type="button" onClick={onClose} aria-label={t('cinema_cancel')} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-800 text-foreground-400 hover:text-foreground-100">
        <i className="ri-close-line text-lg" />
      </button>
      <img src="/images/reward-coin.webp" alt="" aria-hidden="true" className="mx-auto h-20 w-20 object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.5)]" />
      <h2 className="mt-3 text-center text-xl font-bold text-foreground-50">{t('cinema_shareEarnPoints')}</h2>
      <p className="mt-2 text-center text-sm leading-6 text-foreground-300">{t('cinema_shareRewardUnlimited', { points: inviteRewardPoints })}</p>

      {member.isLoggedIn ? <div className="mt-5">
        <p className="mb-2 text-xs font-medium text-foreground-500">{t('cinema_shareLinkLabel')}</p>
        <div className="flex items-stretch gap-2">
          <div className="min-w-0 flex-1 truncate rounded-xl border border-background-800 bg-background-950/70 px-3 py-3 text-xs text-foreground-300">{shareLink || t('cinema_loading')}</div>
          <button type="button" disabled={!shareLink} onClick={() => void copyShareLink()} className="shrink-0 rounded-xl bg-accent-500 px-4 text-sm font-bold text-background-950 hover:bg-accent-500/90 disabled:opacity-50">
            {copied ? t('cinema_copied') : t('cinema_copyLink')}
          </button>
        </div>
      </div> : <button type="button" onClick={() => closeAndNavigate('/login')} className="mt-5 h-12 w-full rounded-xl bg-accent-500 text-sm font-bold text-background-950 hover:bg-accent-500/90">
        {t('cinema_loginForShareLink')}
      </button>}

      <button type="button" onClick={() => closeAndNavigate(member.isLoggedIn ? '/referral' : '/login')} className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-accent-500/60 bg-accent-500/10 text-sm font-bold text-accent-400 transition-colors hover:bg-accent-500/20">
        <i className="ri-line-chart-line text-lg" aria-hidden="true" />
        {t('cinema_viewReferralStats')}
      </button>
    </div>
  </div>;
}
